/**
 * Activity type badge utilities for sport_type, workout_type, trainer.
 */

const SPORT_TYPE_COLORS = {
  Run: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
  TrailRun: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
  Treadmill: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  VirtualRun: 'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20',
  Ride: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
  Swim: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/20',
  Walk: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
  Hike: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20',
  WeightTraining: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20',
  Yoga: 'bg-pink-500/10 text-pink-700 dark:text-pink-400 border-pink-500/20',
};

const RUN_TYPE_BADGE_COLOR = 'bg-muted/80 text-muted-foreground border-border';

export function getActivityColor(type) {
  return SPORT_TYPE_COLORS[type] || 'bg-muted text-muted-foreground border-border';
}

/** Strava uses sport_type `WeightTraining` for gym / strength sessions. */
export function isWeightTrainingActivity(activity) {
  if (!activity) return false;
  const st = String(activity.sport_type || activity.type || '').toLowerCase();
  return st === 'weighttraining';
}

/**
 * Returns badge configs for activity type display.
 * @param {Object} activity - { sport_type, type, workout_type, trainer }
 * @param {Function} t - i18n translate function
 * @returns {{ mainLabel: string, mainColor: string, extraBadges: Array<{ label: string, className: string }> }}
 */
export function getActivityTypeBadges(activity, t) {
  const sportType = activity.sport_type || activity.type || 'Run';
  const workoutType = activity.workout_type != null ? Number(activity.workout_type) : null;
  const trainer = !!activity.trainer;
  const urexRm = String(activity.urex_recording_mode || '').trim().toLowerCase();

  const mainColor = getActivityColor(sportType);

  const sportLabels = {
    Run: 'Run',
    TrailRun: t('activityDetail.runType.trailRun'),
    Treadmill: t('activityDetail.runType.treadmill'),
    VirtualRun: t('activityDetail.runType.virtual'),
    Ride: 'Ride',
    Swim: 'Swim',
    Walk: 'Walk',
    Hike: 'Hike',
  };
  const mainLabel = sportLabels[sportType] || sportType;

  const extraBadges = [];

  if (workoutType === 1) extraBadges.push({ label: t('activityDetail.runType.race'), className: RUN_TYPE_BADGE_COLOR });
  if (workoutType === 2) extraBadges.push({ label: t('activityDetail.runType.longRun'), className: RUN_TYPE_BADGE_COLOR });
  if (workoutType === 3) extraBadges.push({ label: t('activityDetail.runType.workout'), className: RUN_TYPE_BADGE_COLOR });

  // Linked Citius / Stryd planned workout recording_mode (authoritative when present)
  if (urexRm === 'treadmill' && sportType !== 'Treadmill') {
    extraBadges.push({ label: t('activityDetail.runType.treadmill'), className: SPORT_TYPE_COLORS.Treadmill });
  }
  if (urexRm === 'indoor') {
    extraBadges.push({ label: t('activityDetail.runType.indoorNoGps'), className: RUN_TYPE_BADGE_COLOR });
  }

  const labeledFromUrex = urexRm === 'treadmill' || urexRm === 'indoor';
  if (!labeledFromUrex && trainer && !['Treadmill', 'VirtualRun'].includes(sportType)) {
    extraBadges.push({ label: t('activityDetail.runType.indoor'), className: RUN_TYPE_BADGE_COLOR });
  }

  return { mainLabel, mainColor, extraBadges };
}
